import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { Diff } from "@/features/FormCard/Diff";

import {
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";

import { choice_yes_no } from "@/data/choice.ts";

const table_name = "a7_opcvm_underlying_ts";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  a7_opcvm_underlying_id: z.coerce.number().int(),
  sous_jacent_en_defaut: z.string().nullable(),
});

export function A7OPCVMUnderlyingTSEditCard({ id }: { id: number }) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    id,
    table_name,
    FormSchema,
    client: "a7",
    disable_translations_request: true,
    fieldsNotApplyTranslation: ["type_instrument_du_sous_jacent"],
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Underlying time series",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-2">
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <DatePickerField
                    name="date"
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <InputField
                    name="a7_opcvm_underlying_id"
                    label={t(`${table_name}.columns.a7_opcvm_underlying_id`, {
                      ns: "tables",
                      defaultValue: "Instrument financier",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                </div>
                <SelectField
                  name="sous_jacent_en_defaut"
                  label={t(`${table_name}.columns.sous_jacent_en_defaut`, {
                    ns: "tables",
                    defaultValue: "Défaut",
                  })}
                  options={choice_yes_no.map((c) => ({
                    value: c.value,
                    label: c.value,
                  }))}
                  control={form.control}
                />
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  elementId={id}
                  client="a7"
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={changedFields ? Diff(changedFields) : t("no_changes")}
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={closeDeleteModal}
        />
      </div>
    </div>
  );
}
